import { useCallback, useLayoutEffect, useMemo } from 'react';
import { actions, Cell, ensurePluginOrder, makePropGetter, Row, useGetLatest } from 'react-table';

// Actions
actions.resetMaskRevealed = 'resetMaskRevealed';
actions.toggleRowMaskRevealed = 'toggleRowMaskRevealed';
actions.toggleAllRowsMaskRevealed = 'toggleAllRowsMaskRevealed';

export const useMaskRevealed = (hooks: any) => {
  hooks.getToggleAllRowsMaskRevealedProps = [defaultGetToggleAllRowsMaskRevealedProps];
  hooks.getToggleRowMaskRevealedProps = [defaultGetToggleRowMaskRevealedProps];
  hooks.stateReducers.push(reducer);
  hooks.useInstance.push(useInstance);
  hooks.prepareRow.push(prepareRow);
};

useMaskRevealed.pluginName = 'useMaskRevealed';

const defaultGetToggleAllRowsMaskRevealedProps = (props: any, { instance }: any) => [
  props,
  {
    onClick: () => {
      instance.toggleAllRowsMaskRevealed();
    },
  },
];

const defaultGetToggleRowMaskRevealedProps = (props: any, { row }: any) => [
  props,
  {
    onClick: () => {
      row.toggleRowMaskRevealed();
    },
  },
];

// Reducer
function reducer(state: any, action: any, _: any, instance: any) {
  if (action.type === actions.init) {
    return {
      maskRevealed: {},
      ...state,
    };
  }

  if (action.type === actions.resetMaskRevealed) {
    return {
      ...state,
      maskRevealed: instance.initialState.maskRevealed || {},
    };
  }

  if (action.type === actions.toggleAllRowsMaskRevealed) {
    const { value } = action;
    const { rowsById } = instance;

    const isAllRowsMaskRevealed =
      Object.keys(rowsById).length === Object.keys(state.maskRevealed).length;

    const maskRevealAll = typeof value !== 'undefined' ? value : !isAllRowsMaskRevealed;

    if (maskRevealAll) {
      const maskRevealed = {};

      Object.keys(rowsById).forEach((rowId) => {
        // @ts-ignore
        maskRevealed[rowId] = true;
      });

      return {
        ...state,
        maskRevealed,
      };
    }

    return {
      ...state,
      maskRevealed: {},
    };
  }

  if (action.type === actions.toggleRowMaskRevealed) {
    const { id, value: setMaskRevealed } = action;
    const exists = state.maskRevealed[id];

    const shouldExist = typeof setMaskRevealed !== 'undefined' ? setMaskRevealed : !exists;

    if (!exists && shouldExist) {
      return {
        ...state,
        maskRevealed: {
          ...state.maskRevealed,
          [id]: true,
        },
      };
    } else if (exists && !shouldExist) {
      const { [id]: rest } = state.maskRevealed;
      return {
        ...state,
        maskRevealed: rest,
      };
    } else {
      return state;
    }
  }
}

export function useInstance(instance: any) {
  const {
    data,
    rows,
    rowsById,
    getHooks,
    autoResetMaskRevealed = true,
    plugins,
    state: { maskRevealed },
    dispatch,
  } = instance;

  ensurePluginOrder(
    plugins,
    ['useSortBy', 'useGroupBy', 'usePivotColumns', 'useGlobalFilter'],
    'useMaskRevealed'
  );

  const maskRevealedRows = useMemo(() => {
    rows.forEach((row: Row) => {
      if (row.allCells) {
        const canMask = row.allCells
          .map((cell: Cell) => cell.column.id === 'mask')
          .find((item: boolean) => item);

        if (canMask) {
          // @ts-ignore
          row.isMaskRevealed = maskRevealed[row.id];
        }
      }
    });

    return rows;
  }, [rows, maskRevealed]);

  const getAutoResetMaskRevealed = useGetLatest(autoResetMaskRevealed);

  let isAllRowsMaskRevealed = Boolean(
    Object.keys(rowsById).length && Object.keys(maskRevealed).length
  );

  if (isAllRowsMaskRevealed) {
    if (Object.keys(rowsById).some((id) => !maskRevealed[id])) {
      isAllRowsMaskRevealed = false;
    }
  }

  useLayoutEffect(() => {
    if (getAutoResetMaskRevealed()) {
      dispatch({ type: actions.resetMaskRevealed });
    }
  }, [dispatch, data]);

  const toggleRowMaskRevealed = useCallback(
    (id, value) => {
      dispatch({ type: actions.toggleRowMaskRevealed, id, value });
    },
    [dispatch]
  );

  const toggleAllRowsMaskRevealed = useCallback(
    (value) => dispatch({ type: actions.toggleAllRowsMaskRevealed, value }),
    [dispatch]
  );

  const getInstance = useGetLatest(instance);

  const getToggleAllRowsMaskRevealedProps = makePropGetter(
    getHooks().getToggleAllRowsMaskRevealedProps,
    {
      instance: getInstance(),
    }
  );

  Object.assign(instance, {
    rows: maskRevealedRows,
    isAllRowsMaskRevealed,
    toggleRowMaskRevealed,
    toggleAllRowsMaskRevealed,
    getToggleAllRowsMaskRevealedProps,
  });
}

function prepareRow(row: any, { instance: { getHooks }, instance }: any) {
  row.toggleRowMaskRevealed = (set: any) => instance.toggleRowMaskRevealed(row.id, set);

  row.getToggleRowMaskRevealedProps = makePropGetter(getHooks().getToggleRowMaskRevealedProps, {
    instance,
    row,
  });
}
